import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Icon,
  InputLabel,
  FilledInput,
  FormControl,
  Snackbar,
  Typography,
  Backdrop,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Alert } from '@material-ui/lab';
import * as yup from 'yup';
import clsx from 'clsx';
import Logo from './Logo';

interface IProps {
  registration?: boolean;
  onSubmit: (email: string, password: string) => void;
  onLoginWith: (provider: string) => void;
  submitting: boolean;
}
export interface IHandler {
  setError: (error: string) => void;
}

const useStyles = makeStyles({
  button: {
    borderRadius: 16,
    color: 'white',
    marginRight: 16,
    paddingLeft: 16,
    paddingRight: 16,
  },
  icon: {
    marginRight: 8,
  },
  input: {
    color: '#FFF',
  },
  link: {
    '&:link': {
      textDecoration: 'none',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
});

const AuthForm: React.ForwardRefRenderFunction<IHandler, IProps> = (
  { registration, onSubmit, onLoginWith, submitting },
  ref
) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const { button, icon, input, link, loading } = useStyles();
  useImperativeHandle(ref, () => ({
    setError: (error: string) => {
      setError(error);
      setShowSnackbar(true);
    },
  }));
  const loginWithGoogle = async () => {
    onLoginWith('google');
  };
  const loginWithFacebook = async () => {
    onLoginWith('facebook');
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (registration && password !== confirm) {
      setError('password and confirm password does not match');
      setShowSnackbar(true);
      return;
    }

    const schema = yup.object().shape({
      email: yup.string().required().email(),
      password: yup.string().required().min(6),
    });

    try {
      await schema.validate({ email, password });
    } catch (e) {
      setError(e.message);
      setShowSnackbar(true);
      return;
    }
    onSubmit(email, password);
  };

  return (
    <>
      <Box display='flex' justifyContent='center' mt={2} mb={4}>
        <Link to='/'>
          <Logo />
        </Link>
      </Box>
      <Box bgcolor='#564F70' borderRadius={8} px={4} pt={4} pb={4}>
        <Typography align='center'>
          {registration ? 'ลงทะเบียนด้วย' : 'เข้าสู่ระบบด้วย'}
        </Typography>
        <Box mt={3}>
          <Button
            classes={{ root: button }}
            style={{ backgroundColor: '#1778F2' }}
            onClick={loginWithFacebook}
          >
            <Icon className={clsx('fab fa-facebook', icon)} /> FACEBOOK
          </Button>
          <Button
            classes={{ root: button }}
            style={{ backgroundColor: '#DB4437' }}
            onClick={loginWithGoogle}
          >
            <Icon className={clsx('fab fa-google', icon)} /> GOOGLE
          </Button>
        </Box>
        <Box my={3}>
          <Typography align='center'>หรือ</Typography>
        </Box>
        <form onSubmit={submit}>
          <Box my={3} display='flex'>
            <FormControl variant='filled' fullWidth>
              <InputLabel>อีเมล</InputLabel>
              <FilledInput
                classes={{ root: input }}
                value={email}
                onChange={(e) => {
                  const { value } = e.target;
                  setEmail(value);
                }}
              />
            </FormControl>
          </Box>
          <Box mt={3} mb={1}>
            <FormControl variant='filled' fullWidth>
              <InputLabel>รหัสผ่าน</InputLabel>
              <FilledInput
                classes={{ root: input }}
                type='password'
                value={password}
                onChange={(e) => {
                  const { value } = e.target;
                  setPassword(value);
                }}
              />
            </FormControl>
          </Box>
          {registration ? (
            <Box mt={3} mb={1}>
              <FormControl variant='filled' fullWidth>
                <InputLabel>ยืนยันรหัสผ่าน</InputLabel>
                <FilledInput
                  classes={{ root: input }}
                  type='password'
                  value={confirm}
                  onChange={(e) => {
                    const { value } = e.target;
                    setConfirm(value);
                  }}
                />
              </FormControl>
            </Box>
          ) : (
            <Link className={link} to='/reset-password'>
              <Typography color='secondary'>ลืมรหัสผ่าน?</Typography>
            </Link>
          )}
          <Box my={3}>
            <Button
              classes={{ root: button }}
              variant='contained'
              fullWidth
              style={{ backgroundColor: '#7683F9' }}
              type='submit'
            >
              {registration ? 'สร้างบัญชี' : 'เข้าสู่ระบบ'}
            </Button>
          </Box>
        </form>
      </Box>
      <Backdrop open={submitting} className={loading}>
        <CircularProgress color='secondary' />
      </Backdrop>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => {
          setShowSnackbar(false);
          setError('');
        }}
      >
        <Alert severity='warning'>{error}</Alert>
      </Snackbar>
    </>
  );
};

export default forwardRef(AuthForm);
